import React, { useEffect, useState } from 'react';
import { transactionApi, casinoApi } from '../services/api';
import { Transaction, Casino } from '../types';
import './Transactions.css';

interface CasinoEntry {
  id: number;
  casino_id: string;
  depositAmount: string;
  withdrawalAmount: string;
}

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [casinos, setCasinos] = useState<Casino[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Multi-Casino Form state
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formNote, setFormNote] = useState('');
  const [casinoEntries, setCasinoEntries] = useState<CasinoEntry[]>([]);

  // Edit form state (für Bearbeitung einer einzelnen Transaktion)
  const [editFormData, setEditFormData] = useState({
    casino_id: '',
    type: 'deposit' as 'deposit' | 'withdrawal',
    amount: '',
    note: '',
    transaction_date: new Date().toISOString().split('T')[0]
  });

  // Filter state
  const [filterCasino, setFilterCasino] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, casinoRes] = await Promise.all([
        transactionApi.getAll(),
        casinoApi.getAll()
      ]);
      setTransactions(transRes.data);
      setCasinos(casinoRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilteredTransactions = async () => {
    try {
      const params: any = {};
      if (filterCasino) params.casinoId = filterCasino;
      if (filterType) params.type = filterType;

      const response = await transactionApi.getAll(params);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      fetchFilteredTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCasino, filterType]);

  const createInitialEntry = (): CasinoEntry => ({
    id: Date.now(),
    casino_id: casinos[0]?.id.toString() || '',
    depositAmount: '',
    withdrawalAmount: ''
  });

  const resetForm = () => {
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormNote('');
    setCasinoEntries([]);
    setEditFormData({
      casino_id: casinos[0]?.id.toString() || '',
      type: 'deposit',
      amount: '',
      note: '',
      transaction_date: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addCasinoEntry = () => {
    setCasinoEntries([...casinoEntries, createInitialEntry()]);
  };

  const removeCasinoEntry = (id: number) => {
    if (casinoEntries.length > 1) {
      setCasinoEntries(casinoEntries.filter(entry => entry.id !== id));
    }
  };

  const updateCasinoEntry = (id: number, field: keyof CasinoEntry, value: string) => {
    setCasinoEntries(casinoEntries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const promises: Promise<any>[] = [];

    casinoEntries.forEach(entry => {
      // Einzahlung erstellen wenn Betrag eingegeben
      if (entry.depositAmount && parseFloat(entry.depositAmount) > 0) {
        promises.push(
          transactionApi.create({
            casino_id: parseInt(entry.casino_id),
            type: 'deposit',
            amount: parseFloat(entry.depositAmount),
            note: formNote || undefined,
            transaction_date: formDate
          })
        );
      }

      // Auszahlung erstellen wenn Betrag eingegeben
      if (entry.withdrawalAmount && parseFloat(entry.withdrawalAmount) > 0) {
        promises.push(
          transactionApi.create({
            casino_id: parseInt(entry.casino_id),
            type: 'withdrawal',
            amount: parseFloat(entry.withdrawalAmount),
            note: formNote || undefined,
            transaction_date: formDate
          })
        );
      }
    });

    if (promises.length === 0) {
      alert('Bitte geben Sie mindestens einen Betrag ein.');
      return;
    }

    try {
      await Promise.all(promises);
      resetForm();
      fetchFilteredTransactions();
    } catch (error) {
      console.error('Error saving transactions:', error);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingId) return;

    const data = {
      casino_id: parseInt(editFormData.casino_id),
      type: editFormData.type,
      amount: parseFloat(editFormData.amount),
      note: editFormData.note || undefined,
      transaction_date: editFormData.transaction_date
    };

    try {
      await transactionApi.update(editingId, data);
      resetForm();
      fetchFilteredTransactions();
    } catch (error) {
      console.error('Error updating transaction:', error);
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditFormData({
      casino_id: transaction.casino_id.toString(),
      type: transaction.type,
      amount: transaction.amount.toString(),
      note: transaction.note || '',
      transaction_date: transaction.transaction_date.split('T')[0]
    });
    setEditingId(transaction.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Transaktion wirklich löschen?')) return;

    try {
      await transactionApi.delete(id);
      setTransactions(transactions.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const casinoInitials = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

  const formatDateHeading = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Group transactions by calendar day (transactions arrive newest-first).
  const groupedTransactions = React.useMemo(() => {
    const groups: { date: string; items: Transaction[] }[] = [];
    transactions.forEach((t) => {
      const key = t.transaction_date.split('T')[0];
      const last = groups[groups.length - 1];
      if (last && last.date === key) {
        last.items.push(t);
      } else {
        groups.push({ date: key, items: [t] });
      }
    });
    return groups;
  }, [transactions]);

  const openNewForm = () => {
    if (casinos.length === 0) {
      alert('Bitte fügen Sie zuerst ein Casino hinzu.');
      return;
    }
    setEditingId(null);
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormNote('');
    setCasinoEntries([createInitialEntry()]);
    setShowForm(true);
  };

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="transactions-page">
      <div className="transactions-header">
        <h1>Transaktionen</h1>
        <button className="btn-new" onClick={openNewForm}>
          + Neue Transaktion
        </button>
      </div>

      {showForm && !editingId && (
        <div className="transaction-form-overlay">
          <div className="transaction-form transaction-form-wide">
            <h2>Neue Transaktionen</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Notiz (optional, für alle)</label>
                  <input
                    type="text"
                    value={formNote}
                    onChange={(e) => setFormNote(e.target.value)}
                    placeholder="z.B. Bonus, Freispiele..."
                  />
                </div>
              </div>

              <div className="casino-entries">
                <div className="casino-entries-header">
                  <span>Casino-Einträge</span>
                  <button type="button" className="btn-add-entry" onClick={addCasinoEntry}>
                    + Casino hinzufügen
                  </button>
                </div>

                {casinoEntries.map((entry) => (
                  <div key={entry.id} className="casino-entry">
                    <div className="casino-entry-row">
                      <div className="form-group casino-select-group">
                        <label>Casino</label>
                        <select
                          value={entry.casino_id}
                          onChange={(e) => updateCasinoEntry(entry.id, 'casino_id', e.target.value)}
                          required
                        >
                          {casinos.map((casino) => (
                            <option key={casino.id} value={casino.id}>
                              {casino.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Einzahlung (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={entry.depositAmount}
                          onChange={(e) => updateCasinoEntry(entry.id, 'depositAmount', e.target.value)}
                          placeholder="0.00"
                          className="input-deposit"
                        />
                      </div>
                      <div className="form-group">
                        <label>Auszahlung (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={entry.withdrawalAmount}
                          onChange={(e) => updateCasinoEntry(entry.id, 'withdrawalAmount', e.target.value)}
                          placeholder="0.00"
                          className="input-withdrawal"
                        />
                      </div>
                      {casinoEntries.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-entry"
                          onClick={() => removeCasinoEntry(entry.id)}
                          title="Eintrag entfernen"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">
                  Alle hinzufügen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showForm && editingId && (
        <div className="transaction-form-overlay">
          <div className="transaction-form">
            <h2>Transaktion bearbeiten</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Casino</label>
                  <select
                    value={editFormData.casino_id}
                    onChange={(e) => setEditFormData({ ...editFormData, casino_id: e.target.value })}
                    required
                  >
                    {casinos.map((casino) => (
                      <option key={casino.id} value={casino.id}>
                        {casino.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={editFormData.type}
                    onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value as 'deposit' | 'withdrawal' })}
                  >
                    <option value="deposit">Einzahlung</option>
                    <option value="withdrawal">Auszahlung</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Betrag (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editFormData.amount}
                    onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={editFormData.transaction_date}
                    onChange={(e) => setEditFormData({ ...editFormData, transaction_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notiz (optional)</label>
                <input
                  type="text"
                  value={editFormData.note}
                  onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                  placeholder="z.B. Bonus, Freispiele..."
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="filters">
        <select
          value={filterCasino}
          onChange={(e) => setFilterCasino(e.target.value)}
        >
          <option value="">Alle Casinos</option>
          {casinos.map((casino) => (
            <option key={casino.id} value={casino.id}>
              {casino.name}
            </option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Alle Typen</option>
          <option value="deposit">Einzahlungen</option>
          <option value="withdrawal">Auszahlungen</option>
        </select>
      </div>

      <div className="transactions-list">
        {transactions.length === 0 ? (
          <p className="no-data">Keine Transaktionen vorhanden</p>
        ) : (
          groupedTransactions.map((group) => (
            <div key={group.date} className="day-group">
              <div className="day-heading">{formatDateHeading(group.date)}</div>
              <div className="day-cards">
                {group.items.map((transaction) => {
                  const isWithdrawal = transaction.type === 'withdrawal';
                  return (
                    <div key={transaction.id} className="transaction-card">
                      <div className="tc-logo">
                        {transaction.casino_logo ? (
                          <img
                            src={transaction.casino_logo}
                            alt={transaction.casino_name}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              img.parentElement?.classList.add('tc-logo--fallback');
                            }}
                          />
                        ) : (
                          <span className="tc-logo-initials">
                            {casinoInitials(transaction.casino_name)}
                          </span>
                        )}
                      </div>

                      <div className="tc-main">
                        <div className="tc-casino-name">{transaction.casino_name}</div>
                        <div className="tc-meta">
                          <span className={`type-badge ${transaction.type}`}>
                            {isWithdrawal ? 'Auszahlung' : 'Einzahlung'}
                          </span>
                          {transaction.note && (
                            <span className="tc-note" title={transaction.note}>
                              {transaction.note}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className={`tc-amount ${isWithdrawal ? 'positive' : 'negative'}`}>
                        {isWithdrawal ? '+' : '−'}
                        {formatCurrency(transaction.amount)}
                      </div>

                      <div className="tc-actions">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(transaction)}
                          title="Bearbeiten"
                          aria-label="Bearbeiten"
                        >
                          ✎
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(transaction.id)}
                          title="Löschen"
                          aria-label="Löschen"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Transactions;
